import {
  GetUserCredentialsDocument,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'
import { useEffect, useState } from 'react'
import { useApolloClient } from '@apollo/client'
import { uniqueId } from '@sketch/utils'
import isEqual from 'lodash.isequal'
import { localStorageKeys } from '@sketch/constants'
import {
  getActiveAuthorization,
  getAllAuthorizations,
  isPersonalAuthorization,
  isSsoAuthorization,
} from './multisession'

interface HasAccessToWorkspaceProps
  extends Pick<WorkspaceMinimalFragment, 'identifier' | 'userRole'> {
  customer: { ssoEnabled: boolean } | null
}

const useReactiveAuthorizations = () => {
  const [authorizations, setAuthorizations] = useState(getAllAuthorizations())

  useEffect(() => {
    const onStorageChange = (event: StorageEvent) => {
      if (event.key === localStorageKeys.userAllAuthorizations) {
        setAuthorizations(getAllAuthorizations())
      }
    }

    window.addEventListener('storage', onStorageChange)
    return () => {
      window.removeEventListener('storage', onStorageChange)
    }
  }, [])

  return authorizations
}

/**
 * Returns all authorizations for the user.
 * It also returns some helpful properties in case you don't want to deal with
 * authorizations directly and just want to know what kind of sessions the user
 * has.
 */
export const useUserAuthorizations = () => {
  const authorizations = useReactiveAuthorizations()
  const hasPersonalAuthorization = authorizations.some(isPersonalAuthorization)
  const { cache } = useApolloClient()
  const [activeAuthorization, setActiveAuthorization] = useState(
    getActiveAuthorization(cache)
  )

  const ssoWorkspaceIds = authorizations
    .filter(isSsoAuthorization)
    .map(session => session.workspaceId)

  useEffect(() => {
    // Try to check if there's data in cache first
    let hasData = false
    try {
      const existingData = cache.readQuery({
        query: GetUserCredentialsDocument,
        variables: { key: uniqueId() },
      })
      hasData = !!existingData
    } catch {
      hasData = false
    }

    if (!hasData) return

    // For Apollo v3, we'll use a different approach - polling or subscription
    // This is a simplified version - in practice you might want to use a proper Apollo client
    const checkForUpdates = () => {
      try {
        const newActiveAuth = getActiveAuthorization(cache)
        if (!isEqual(newActiveAuth?.fragment, activeAuthorization?.fragment)) {
          setActiveAuthorization(newActiveAuth)
        }
      } catch {
        // Handle silently
      }
    }

    // Check immediately and set up periodic checks
    checkForUpdates()
    const interval = setInterval(checkForUpdates, 1000) // Check every second

    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAuthorization, cache])

  return {
    authorizations,
    activeAuthorization,
    hasPersonalAuthorization,
    hasAccessToWorkspace: ({
      identifier,
      customer,
      userRole,
    }: HasAccessToWorkspaceProps) => {
      const isPersonalWorkspaceWithPersonalAuth =
        !customer && hasPersonalAuthorization

      const nonSSOWorkspaceWithPersonalAuth =
        !customer?.ssoEnabled && hasPersonalAuthorization

      const ssoWorkspaceWithSSOAuth =
        customer?.ssoEnabled && ssoWorkspaceIds.includes(identifier)

      const ssoWorkspaceUserIsGuestPersonalAuth =
        customer?.ssoEnabled && userRole === 'GUEST' && hasPersonalAuthorization

      const ssoWorkspaceUserIsAdminPersonalAuth =
        customer?.ssoEnabled && hasPersonalAuthorization && userRole === 'ADMIN'

      const ssoWorkspaceUserIsPartnerPersonalAuth =
        customer?.ssoEnabled &&
        hasPersonalAuthorization &&
        userRole === 'PARTNER'

      return (
        // This is a personal workspace and user has personal auth
        isPersonalWorkspaceWithPersonalAuth ||
        // It's a non-SSO workspace and user has personal auth
        nonSSOWorkspaceWithPersonalAuth ||
        // It's an SSO workspace and the user has an SSO auth for that workspace
        ssoWorkspaceWithSSOAuth ||
        // User is guest in an SSO workspace, and it has a personal auth
        ssoWorkspaceUserIsGuestPersonalAuth ||
        // User is admin of an SSO workspace, and it has a personal auth
        ssoWorkspaceUserIsAdminPersonalAuth ||
        // User is partner of an SSO workspace
        ssoWorkspaceUserIsPartnerPersonalAuth
      )
    },
  }
}

export type UserAuthorizations = ReturnType<typeof useUserAuthorizations>
